import React, { useState, useEffect } from 'react';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import shomriLogo from '@/assets/shomri-logo.png';

type ResetStep = 'email' | 'code';

const Auth: React.FC = () => {
  const {
    isLoaded: authLoaded,
    isSignedIn
  } = useAuth();
  const {
    signIn,
    isLoaded: signInLoaded,
    setActive
  } = useSignIn();
  const navigate = useNavigate();
  const location = useLocation();

  // Sign in state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetStep, setResetStep] = useState<ResetStep>('email');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Get the intended destination from state, or default to "/"
  const from = (location.state as {
    from?: {
      pathname: string;
    };
  })?.from?.pathname || '/';

  // Redirect if already signed in
  useEffect(() => {
    if (authLoaded && isSignedIn) {
      navigate(from, {
        replace: true
      });
    }
  }, [authLoaded, isSignedIn, navigate, from]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInLoaded || !signIn) return;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await signIn.create({
        identifier: email,
        password: password
      });
      if (result.status === 'complete') {
        await setActive({
          session: result.createdSessionId
        });
        navigate(from, {
          replace: true
        });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Sign in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInLoaded || !signIn) return;
    setIsSubmitting(true);
    setError('');
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email
      });
      setResetStep('code');
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to send reset code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInLoaded || !signIn) return;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: resetCode,
        password: newPassword
      });
      if (result.status === 'complete') {
        await setActive({
          session: result.createdSessionId
        });
        navigate(from, {
          replace: true
        });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToSignIn = () => {
    setForgotPasswordMode(false);
    setResetStep('email');
    setResetCode('');
    setNewPassword('');
    setError('');
  };

  const handleEnterForgotPassword = () => {
    setForgotPasswordMode(true);
    setPassword('');
    setError('');
  };

  // Show loading while Clerk initializes
  if (!authLoaded || !signInLoaded) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img src={shomriLogo} alt="Shomri" className="h-10 animate-pulse" />
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // If already signed in, show loading while redirecting
  if (isSignedIn) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img src={shomriLogo} alt="Shomri" className="h-10" />
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background safe-area-top safe-area-bottom">
      {/* Decorative gradient bar at top */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[hsl(195,100%,69%)] via-[hsl(255,73%,73%)] via-[hsl(160,100%,50%)] via-[hsl(340,82%,63%)] to-[hsl(42,90%,60%)]" />
      
      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* Logo Section */}
        <div className="mb-12 flex flex-col items-center">
          <img src={shomriLogo} alt="Shomri" className="h-12 mb-6" />
          <h1 className="text-2xl font-semibold text-foreground">
            {forgotPasswordMode 
              ? (resetStep === 'email' ? 'Reset Password' : 'Enter Code')
              : 'Welcome back'
            }
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {forgotPasswordMode 
              ? (resetStep === 'email' 
                  ? "Enter your email to receive a reset code" 
                  : "Check your email for the 6-digit code")
              : 'Sign in to continue'
            }
          </p>
        </div>

        {/* Forms */}
        <div className="w-full max-w-sm">
          {!forgotPasswordMode ? (
            /* Sign In Form */
            <form onSubmit={handleSignIn} className="space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12 text-base rounded-xl border-border bg-background focus:border-primary focus:ring-primary/20"
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="h-12 text-base rounded-xl border-border bg-background pr-12 focus:border-primary focus:ring-primary/20"
                    style={{ fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleEnterForgotPassword}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive text-center">{error}</p>
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary-hover text-primary-foreground shadow-medium transition-all duration-200 active:scale-[0.98]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : 'Sign In'}
              </Button>
            </form>
          ) : resetStep === 'email' ? (
            /* Forgot Password - Email Step */
            <form onSubmit={handleSendResetCode} className="space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="reset-email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12 text-base rounded-xl border-border bg-background focus:border-primary focus:ring-primary/20"
                  style={{ fontSize: '16px' }}
                />
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive text-center">{error}</p>
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary-hover text-primary-foreground shadow-medium transition-all duration-200 active:scale-[0.98]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending code...</span>
                  </div>
                ) : 'Send Reset Code'}
              </Button>

              {/* Back to Sign In */}
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          ) : (
            /* Forgot Password - Code Step */
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Code Input */}
              <div className="space-y-2">
                <label htmlFor="reset-code" className="text-sm font-medium text-foreground">
                  Reset Code
                </label>
                <Input
                  id="reset-code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={e => setResetCode(e.target.value)}
                  required
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  className="h-12 text-base rounded-xl border-border bg-background focus:border-primary focus:ring-primary/20 text-center tracking-widest"
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* New Password Input */}
              <div className="space-y-2">
                <label htmlFor="new-password" className="text-sm font-medium text-foreground">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="h-12 text-base rounded-xl border-border bg-background pr-12 focus:border-primary focus:ring-primary/20"
                    style={{ fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive text-center">{error}</p>
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary-hover text-primary-foreground shadow-medium transition-all duration-200 active:scale-[0.98]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Resetting...</span>
                  </div>
                ) : 'Reset Password'}
              </Button>

              {/* Back to Sign In */}
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
